import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { postCloseWithExceptionFile } from 'api/claimAPI';
import { handleError } from 'services/Error';
import { useGetPayersQuery } from 'store/api/referenceApi';

export const CloseClaims = () => {
    const { data } = useGetPayersQuery();
    const { payerList } = data ?? {};

    const [uploadError, setUploadError] = useState();

    return (
        <FormHeaderAndContent
            mainContent={
                <Formik
                    initialValues={{}}
                    enableReinitialize
                    validationSchema={Yup.object({
                        payerId: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        closeClaimsFile: Yup.array()
                            .required(validationStrings.requiredField.errorMessage)
                            .min(1, validationStrings.requiredField.errorMessage),
                    })}
                    onSubmit={async (values) => {
                        try {
                            setUploadError();
                            await postCloseWithExceptionFile({
                                payerId: values.payerId,
                                file: values.fileToUpload,
                            });

                            SuccessToast({ message: 'Close claims file uploaded successfully.' });
                        } catch (err) {
                            const uploadErr = JSON.parse(err.message ?? {})?.detail;
                            if (uploadErr) {
                                setUploadError(uploadErr);
                                handleError('Error uploading file.', err);
                            } else {
                                handleError(
                                    'An unexpected error occurred while uploading the file',
                                    err,
                                );
                            }
                        }
                    }}
                >
                    {(formik) => (
                        <FormContainer
                            isLoading={!payerList}
                            formTitle="Close Claims with Exception"
                            primaryButtonLabel="Upload"
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                            formLeftContent={
                                <>
                                    <RadioButtonGroupFormik
                                        name="payerId"
                                        label="Payer"
                                        required
                                        options={payerList}
                                    />
                                    {uploadError && (
                                        <div className="margin__bottom--medium">
                                            <InlineMessage
                                                intent={InlineMessage.Intent.ERROR}
                                                title="Error Uploading File"
                                                label={
                                                    <div
                                                        className="flex__gap--small"
                                                        style={{ flexDirection: 'column' }}
                                                    >
                                                        {uploadError.map((err) => (
                                                            <div key={err.error_message}>
                                                                {err.error_message}
                                                                <ul style={{ margin: 0 }}>
                                                                    {err.invalid_rows?.map(
                                                                        (row) => (
                                                                            <li
                                                                                key={row.row_number}
                                                                            >{`Row ${row.row_number} - ${row.error}`}</li>
                                                                        ),
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        ))}
                                                    </div>
                                                }
                                            />
                                        </div>
                                    )}
                                    <FileUploadFormik
                                        id="closeClaimsFile"
                                        label="Close Claims File"
                                        required
                                        accept=".csv"
                                        maxAllowedFiles={1}
                                        onAddFiles={(files) =>
                                            formik.setFieldValue('fileToUpload', files?.[0])
                                        }
                                        onRemoveFile={() =>
                                            formik.setFieldValue('fileToUpload', undefined)
                                        }
                                    />
                                </>
                            }
                        />
                    )}
                </Formik>
            }
        />
    );
};
